/**
 * Calculate labour hours for a shift (excluding lunch).
 * @param {string} startTime - "HH:MM" format
 * @param {string} endTime - "HH:MM" format
 * @param {number} lunchMinutes - lunch break in minutes
 * @returns {number} hours worked (excluding lunch)
 */
export function calcShiftHours(startTime, endTime, lunchMinutes = 0) {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  let totalMinutes = (eh * 60 + em) - (sh * 60 + sm);
  if (totalMinutes < 0) totalMinutes += 24 * 60; // overnight shifts
  return Math.max(0, (totalMinutes - (lunchMinutes || 0)) / 60);
}

/**
 * Calculate total available and allocated labour hours for a list of shifts.
 * Available = sum of (shift_hours × max_capacity)
 * Allocated = sum of (shift_hours × assigned_count)
 * All hours exclude lunches.
 */
export function calcWeekLabourHours(shifts) {
  let available = 0;
  let allocated = 0;
  for (const s of shifts) {
    const hours = calcShiftHours(s.start_time, s.end_time, s.lunch_minutes || 0);
    const cap = s.max_capacity || 1;
    const assigned = s.assigned_users?.length || (s.assigned_username ? 1 : 0);
    available += hours * cap;
    allocated += hours * assigned;
  }
  return { available: Math.round(available * 10) / 10, allocated: Math.round(allocated * 10) / 10 };
}

/**
 * Calculate total labour hours for a template week definition.
 * Each template shift contributes: shift_hours × max_capacity
 */
export function calcTemplateWeekHours(templateShifts) {
  let total = 0;
  for (const s of (templateShifts || [])) {
    const hours = calcShiftHours(s.start_time, s.end_time, s.lunch_minutes || 0);
    total += hours * (s.max_capacity || 1);
  }
  return Math.round(total * 10) / 10;
}

/**
 * Calculate labour hours per day for template week shifts.
 * Returns array of 7 numbers (Mon=0..Sun=6).
 */
export function calcTemplateWeekDayHours(templateShifts) {
  const dayHours = [0, 0, 0, 0, 0, 0, 0];
  for (const s of (templateShifts || [])) {
    const hours = calcShiftHours(s.start_time, s.end_time, s.lunch_minutes || 0);
    const day = s.day_of_week;
    if (day >= 0 && day <= 6) {
      dayHours[day] += hours * (s.max_capacity || 1);
    }
  }
  return dayHours.map(h => Math.round(h * 10) / 10);
}
